/**
 * ENG-8198: decide whether an agent is ACTUALLY rate-limited, from its
 * transcript rather than from pixels.
 *
 * ENG-8201 (Slice 1) moved the CLASSIFICATION into `@augmented/core`'s
 * `classifyTranscriptRateLimit` — the channel MCP servers are now a second
 * consumer of it (they watch for a refusal AFTER dispatching, instead of the
 * manager predicting one before), and two implementations of "was this turn
 * refused for the cap" would drift silently. Everything left in this file is the
 * manager's fs wrapper: which files to open, and how far back to look. The
 * reasoning behind the classification itself — why the transcript and not the
 * pane, and why the refusal's zero-valued `usage` block is a trap — lives with
 * the shared function.
 */

import { readFileSync, readdirSync, statSync } from 'node:fs';
import { join } from 'node:path';
import {
  UNKNOWN_RATE_LIMIT,
  classifyTranscriptRateLimit,
  pickNewerClassification,
  type RateLimitClassification,
  type RateLimitVerdict,
} from '@augmented/core';
import { sessionTranscriptDir } from './daily-session.js';

export type { RateLimitVerdict };

/**
 * How far back to look for evidence.
 *
 * Sized to the thing being detected, not to the poll cadence (ENG-8202). A cap
 * lasts hours, but the evidence for it is a SINGLE refusal at the moment it was
 * hit — and once the marker suppresses dispatch, no further turns are attempted,
 * so no fresh refusals are ever written. A short window therefore decays a real
 * cap to `unknown` within minutes: on the DTI host, three genuinely-capped agents
 * had refusals 5-6 hours old and classified as `unknown` under the original
 * 15-minute window.
 *
 * Widening is nearly free because the verdict takes the NEWEST classified turn:
 * any later successful turn flips it to `serving` immediately, so a long window
 * cannot pin a lifted cap open. The only case it holds `capped` longer is an idle
 * agent with no contradicting evidence — and the marker carries `limited_until`,
 * which bounds it independently.
 */
export const RATE_LIMIT_WINDOW_MS = 6 * 60 * 60 * 1000;

/**
 * The agent's current rate-limit state per its own transcript, with the reset
 * instant when it is capped.
 *
 * Takes the NEWEST classifiable turn in the window: a cap that has since lifted
 * shows a later successful turn, and an agent that just hit the cap shows a later
 * refusal. Never throws — an unreadable transcript is `unknown`.
 *
 * `transcriptDir` / `now` are injectable for tests.
 */
export function probeRateLimit(args: {
  codeName: string;
  projectDir: string;
  now?: Date;
  windowMs?: number;
  /** Test seam: override the transcript directory. */
  transcriptDir?: string;
}): RateLimitClassification {
  const now = args.now ?? new Date();
  const endMs = now.getTime();
  const startMs = endMs - (args.windowMs ?? RATE_LIMIT_WINDOW_MS);

  const dir = args.transcriptDir ?? sessionTranscriptDir(args.projectDir);

  let entries: string[];
  try {
    entries = readdirSync(dir);
  } catch {
    return UNKNOWN_RATE_LIMIT;
  }

  let newest: RateLimitClassification = UNKNOWN_RATE_LIMIT;

  for (const name of entries) {
    if (!name.endsWith('.jsonl')) continue;
    const path = join(dir, name);

    // mtime is a cheap upper bound on the newest entry: a file untouched since
    // before the window opened cannot hold an in-window turn.
    try {
      const st = statSync(path);
      if (!st.isFile() || st.mtimeMs < startMs) continue;
    } catch {
      continue;
    }

    let content: string;
    try {
      content = readFileSync(path, 'utf-8');
    } catch {
      continue;
    }

    newest = pickNewerClassification(
      newest,
      classifyTranscriptRateLimit(content, startMs, endMs, now),
    );
  }

  return newest;
}

// ENG-8208 removed `probeRateLimitState`, the verdict-only convenience. Its only
// production caller was the marker-arming path, which took the reset instant from
// the banner it had already parsed and so needed nothing but the verdict. The
// gates that replaced it need `resetsAt` too, and read the full classification.

/**
 * ENG-8208: the manager's own usage-cap gate, now reactive.
 *
 * ENG-8197 taught three manager-driven dispatch paths — the kanban tick, the
 * persistent scheduler and the claude scheduler — not to hand work to a capped
 * agent. All three read the ENG-7904 marker, which Slice 4 deletes.
 *
 * They CANNOT simply lose the gate along with it. The channel adapters could go
 * reactive because their harm is a wasted turn: dispatch, see the refusal, report
 * it. The scheduler's harm lands BEFORE any refusal comes back —
 * `fireScheduledTaskViaKanban` calls `markTaskFired`, which recomputes
 * `nextFireAt` from NOW, so a firing during a cap CONSUMES the occurrence on
 * behalf of an agent that never ran it. Watching for the refusal afterwards
 * cannot give the occurrence back. That is the ENG-8197 data loss, and it is
 * independent of how the cap was detected.
 *
 * So the gate stays and only its INPUT changes: from a marker armed off a
 * pane.log screen-scrape to the transcript the agent actually wrote. That is the
 * whole point of ENG-8201 — the scrape could not tell Claude Code's own render
 * from the agent writing ABOUT it (ENG-8198), and a false positive here froze
 * scheduled tasks for hours. `probeRateLimit` reads the refusal Claude Code
 * recorded, which an agent cannot author by discussing it.
 *
 * Cached because the shape of the read changed. The marker was one small file;
 * the probe is a readdir + stat + full read of every in-window transcript, and
 * the kanban tick alone re-evaluates roughly every 10s per agent. A 60s TTL
 * matches the banner monitor's own MIN_CHECK_INTERVAL_MS and bounds staleness
 * well under the hours a cap lasts.
 *
 * FAILS OPEN, in both senses:
 *   - an unreadable transcript classifies `unknown` → no gate, dispatch as before;
 *   - `capped` with NO parseable reset instant → also no gate.
 * The second is the deliberate one. A cap with no bound cannot expire on time,
 * and because gating suppresses the very dispatches that would produce fresh
 * evidence, an unbounded defer could wedge an agent's whole schedule shut with no
 * path back. The marker never had this case (it always carried `limited_until`
 * from the banner). Refusing to gate without a bound is the same call the
 * reactive channel path makes for the same reason: a gate that jams shut is worse
 * than one that occasionally lets a doomed turn through, and the turn is free.
 *
 * `now` / `transcriptDir` / `ttlMs` are injectable for tests.
 */
const USAGE_CAP_PROBE_TTL_MS = 60_000;

interface CachedUsageCapProbe {
  /** Epoch ms the probe ran. */
  at: number;
  classification: RateLimitClassification;
}

const usageCapProbeCache = new Map<string, CachedUsageCapProbe>();

/**
 * ENG-8202, carried across to the reactive input: a cap belongs to the ACCOUNT
 * that hit it.
 *
 * The deleted marker had a lifecycle hook for this — on a signed-in-account
 * change the manager cleared it, because the old account's cap says nothing about
 * the new one. The concern does not go away with the marker; it gets sharper. The
 * probe reads a 6h window of transcript, and a re-login does not rewrite history:
 * the previous account's `rate_limit` refusals are still on disk and still inside
 * the window, so without this they would read as a live cap on a healthy new
 * account.
 *
 * So the hook is repointed rather than removed. Instead of deleting a file it
 * records a FLOOR — evidence older than the account change is not evidence about
 * the current account — and drops the cached verdict so the next call re-probes.
 *
 * A missing fingerprint (api_key / openrouter mode, or a transient read failure —
 * `getClaudeAccountFingerprint` is sticky, so this is rare) is treated as "no
 * information": the baseline is left untouched rather than recorded as a change,
 * so an unreadable config cannot masquerade as a re-login.
 *
 * ## Two known bounds (CodeRabbit, ENG-8208)
 *
 * 1. ORDERING. The floor is set from `ensurePersistentSession`, which resolves
 *    the fingerprint once per cycle. That call precedes the scheduler gate in
 *    the same function, but the kanban tick and the claude scheduler run outside
 *    it, so on the cycle a re-login is first observed they may read one poll's
 *    worth of prior-account evidence. Deliberately not fixed by resolving the
 *    account inside every gate: `getClaudeAccountFingerprint` is an async config
 *    read, and doing it per-gate-per-tick costs far more than the symptom, which
 *    is a single extra deferral — the tick leaves the cards on the board and the
 *    schedulers leave `nextFireAt` in the past, so nothing is consumed or lost.
 *
 * 2. DURABILITY. Both maps are in-memory, so a manager restart forgets the
 *    floor, and a re-login observed across that restart is seen as a first
 *    sighting (a baseline, not a change). Prior-account refusals inside the
 *    window can then gate again. This is a genuine regression against the marker
 *    it replaced, which was a file and survived restarts — accepted here because
 *    the blast radius inverted: the marker's false positive SWALLOWED inbound
 *    messages, whereas this one only delays scheduled work, is bounded by
 *    RATE_LIMIT_WINDOW_MS, and clears the moment any turn is served (channel
 *    dispatch is unconditional now, so ordinary traffic resolves it). Persisting
 *    the floor is the fix if that ever proves too weak.
 */
const claudeAccountByAgent = new Map<string, string>();
const usageCapEvidenceFloorMs = new Map<string, number>();

/**
 * Edge-trigger state for the deferral log lines — see `shouldLogUsageCapDeferral`
 * below for why the gates log on the edge rather than the level.
 *
 * Declared up here with the rest of the module state so `_resetUsageCapProbeCache`
 * can clear it without forward-referencing (CodeRabbit).
 */
const loggedUsageCapDeferral = new Map<string, number>();

/**
 * Test helper — drop ALL module state: the probe cache, the account baselines,
 * and the deferral-dedup map. Not for production use.
 *
 * The dedup map matters as much as the rest: leaving it populated makes a suite's
 * correctness depend on hand-picking unique agent names, and a later test reusing
 * one gets a confusing `false` from `shouldLogUsageCapDeferral` (CodeRabbit).
 */
export function _resetUsageCapProbeCache(): void {
  usageCapProbeCache.clear();
  claudeAccountByAgent.clear();
  usageCapEvidenceFloorMs.clear();
  loggedUsageCapDeferral.clear();
}

export function noteClaudeAccountChange(
  codeName: string,
  fingerprint: string | null,
  log: (msg: string) => void,
  now?: Date,
): void {
  if (!fingerprint) return;
  const previous = claudeAccountByAgent.get(codeName);
  claudeAccountByAgent.set(codeName, fingerprint);
  if (previous === undefined || previous === fingerprint) return;
  usageCapEvidenceFloorMs.set(codeName, (now ?? new Date()).getTime());
  usageCapProbeCache.delete(codeName);
  log(
    `[usage-cap] signed-in Claude account changed for '${codeName}' (${previous} → ${fingerprint}) — ` +
      `disregarding rate-limit evidence recorded before now; the cap belonged to the previous account (ENG-8202)`,
  );
}

export function readUsageCapUntil(args: {
  codeName: string;
  projectDir: string;
  now?: Date;
  windowMs?: number;
  transcriptDir?: string;
  ttlMs?: number;
}): Date | null {
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  // Never look back past a signed-in-account change: those refusals belong to a
  // different account. Narrowing the window (rather than filtering afterwards)
  // means the pre-change turns are not read at all, so they cannot win
  // `pickNewerClassification` either. Immediately after a change the window is
  // ~0, which classifies `unknown` and so fails open — the right direction.
  const floorMs = usageCapEvidenceFloorMs.get(args.codeName);
  const requestedWindowMs = args.windowMs ?? RATE_LIMIT_WINDOW_MS;
  const windowMs =
    floorMs === undefined
      ? requestedWindowMs
      : Math.max(0, Math.min(requestedWindowMs, nowMs - floorMs));

  const cached = usageCapProbeCache.get(args.codeName);
  let classification: RateLimitClassification;
  if (cached && nowMs - cached.at < (args.ttlMs ?? USAGE_CAP_PROBE_TTL_MS)) {
    classification = cached.classification;
  } else {
    classification = probeRateLimit({
      codeName: args.codeName,
      projectDir: args.projectDir,
      now,
      windowMs,
      transcriptDir: args.transcriptDir,
    });
    usageCapProbeCache.set(args.codeName, { at: nowMs, classification });
  }

  if (classification.verdict !== 'capped') return null;

  const resetsAt = classification.resetsAt;
  if (!resetsAt) return null;

  // Time-based expiry, evaluated against the CALLER's clock rather than the
  // cached probe's, so a cap that lapses inside the TTL stops gating on the very
  // next call instead of waiting for the cache to turn over.
  return resetsAt.getTime() > nowMs ? resetsAt : null;
}

/**
 * ENG-8197 (review): edge-trigger for the deferral log lines.
 *
 * The gates that consume `readUsageCapUntil` re-evaluate on every poll — the
 * kanban tick roughly every 10s while the board is non-empty, and the scheduled
 * gates on every tick *by design*, because deferring deliberately leaves
 * `nextFireAt` in the past so the occurrence isn't consumed. Logging
 * unconditionally would therefore emit the same line for the entire cap window:
 * a 16h cap is ~5,700 lines per agent, and the 2026-07-27 incident had ten
 * capped agents at once — ~57,000 lines of decoration on exactly the night the
 * log most needed to be readable.
 *
 * That is the ENG-5080 problem (the per-agent banner, ~26k lines/day) recurring
 * one screen further down the same file, so this reuses its shape: log on the
 * edge, not the level.
 *
 * Keyed by `<site>:<codeName>` so each gate reports its own first deferral —
 * "the tick is skipping" and "scheduled tasks are deferring" are different
 * operational facts and a reader wants both once.
 *
 * Deduped on the `limited_until` VALUE, so a genuinely new cap window logs
 * again. No eviction is needed: entries are bounded by (agents × sites), and
 * `limited_until` is a wall-clock reset instant, so a later cap effectively
 * never repeats an earlier value.
 *
 * Moved here from `usage-limit-marker-file.ts` when ENG-8208 deleted that
 * module: it belongs with whatever supplies the gates' input, which is now the
 * transcript probe. Its state map is declared with the other module state above,
 * so `_resetUsageCapProbeCache` can clear it.
 */
export function shouldLogUsageCapDeferral(
  site: string,
  codeName: string,
  limitedUntil: Date,
): boolean {
  const key = `${site}:${codeName}`;
  const at = limitedUntil.getTime();
  if (loggedUsageCapDeferral.get(key) === at) return false;
  loggedUsageCapDeferral.set(key, at);
  return true;
}
